/* eslint-disable react/jsx-one-expression-per-line */
import React, {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { Input, ConfigProvider, Tooltip, Select, Button } from 'antd';

import { useTranslation } from 'react-i18next';
import { InfoCircleOutlined, PlusCircleOutlined } from '@ant-design/icons';
import 'moment/locale/pt-br';
import Table from '@components/Table';
import moment from 'moment';
import ptBR from 'antd/es/locale/pt_BR';
import useSwr from '@hooks/useSwr';
import useSearchParams from '@hooks/useSearchParams';
import QuickRangePicker from '@components/DatePicker/QuickRangePicker';
import { dateFormat, toISOStringWithOffset } from '@utils/formatters';
import RequestReport from '@components/RequestReport';
import rolesConstants from '@utils/rolesConstants';
import '../SmsRequests/styles.less';
import DeviceActionModal from '@components/DeviceActionModal';
import ActionTypes from '@utils/deviceActions';

const { Search } = Input;

type SmsSendDetailsType = {
  setTableToShow: Dispatch<SetStateAction<string>>;
  handleTabsChange: (key: string) => void;
  tableToShow: string;
};

type SendSmsRequest = {
  id: string;
  from: string;
  message: string;
  status: string;
  statusDetailed: string;
  flash: boolean;
  validityMinutes: number;
  createdBy: string;
  createdAt: string;
  deliveryTimeWindowFrom: string;
  deliveryTimeWindowTo: string;
  deliveryTimeWindowDays: string[];
  sendAt: string;
};

const SmsSendDetails = ({
  setTableToShow,
  tableToShow,
  handleTabsChange,
}: SmsSendDetailsType) => {
  const [msisdnValue, setMsisdnValue] = useState('');
  const [sendSmsRequestIdValue, setSendSmsRequestIdValue] = useState<
    string | undefined
  >();
  const [visible, setVisible] = useState(false);
  const { t } = useTranslation('sms-send');

  const {
    setSearchParams,
    hasValuesInExtraParams,
    clearSearchParams,
    searchParams,
  } = useSearchParams({
    type: 'pagination',
    extraParams: [
      'sendSmsRequestId',
      'msisdn',
      'sentAtFrom',
      'sentAtTo',
      'status',
    ],
  });

  const {
    sendSmsRequestId,
    msisdn,
    sentAtFrom,
    sentAtTo,
    status,
    page,
    linesPerPage,
  } = useMemo(() => searchParams, [searchParams]);

  const filters = useMemo(() => {
    const allFilters = {
      sendSmsRequestId,
      msisdn,
      sentAtFrom,
      sentAtTo,
      status: status?.toUpperCase(),
      page: page ? Number(page) - 1 : 0,
      size: linesPerPage ?? 20,
    };

    return allFilters;
  }, [
    linesPerPage,
    page,
    sendSmsRequestId,
    msisdn,
    sentAtFrom,
    sentAtTo,
    status,
  ]);

  const { data, mutate } = useSwr(`/service-proxy/sms`, {
    params: filters,
  });

  const smsDescriptionLink = useCallback(
    (sendSmsRequestIdToRedirect: string) => (
      <Tooltip title={t('view-sms')}>
        <Button
          type="link"
          onClick={() => {
            setSearchParams({ id: sendSmsRequestIdToRedirect });
            setTableToShow('batch');
          }}
        >
          {sendSmsRequestIdToRedirect}
        </Button>
      </Tooltip>
    ),
    [t, setTableToShow, setSearchParams],
  );

  const columns = useMemo(() => {
    const defaultColumns = [
      {
        title: t('table.sendSmsRequestId'),
        dataIndex: 'sendSmsRequestId',
        render: smsDescriptionLink,
      },
      { title: t('table.msisdn'), dataIndex: 'msisdn' },
      {
        title: t('table.status'),
        dataIndex: 'status',
        render: (statusRecord: string, record: SendSmsRequest) =>
          record.statusDetailed ? (
            <Tooltip title={t(`status-type.details.${record.statusDetailed}`)}>
              <span style={{ marginRight: '3px' }}>
                {t(`status-type.${statusRecord}`)}
              </span>
              <InfoCircleOutlined />
            </Tooltip>
          ) : (
            <span>{t(`status-type.${statusRecord}`)}</span>
          ),
      },
      {
        title: t('table.response'),
        dataIndex: 'response',
      },
      {
        title: t('table.respondedAt'),
        dataIndex: 'respondedAt',
        render: (value: string) => value && dateFormat(value),
      },
      {
        title: t('table.templateVars'),
        dataIndex: 'templateVars',
        render: (templateVars: { [key: string]: string }) => {
          const keys = Object.keys(templateVars);
          if (keys?.length === 0) return '';
          if (keys.length === 1) {
            return (
              <div>
                {Object.entries(templateVars).map(([key, value]) => (
                  <p key={key}>
                    <strong>{key}</strong>: {value}
                  </p>
                ))}
              </div>
            );
          }
          return (
            <Tooltip
              title={
                <div>
                  {Object.entries(templateVars).map(([key, value]) => (
                    <p key={key}>
                      <strong>{key}</strong>: {value}
                    </p>
                  ))}
                </div>
              }
            >
              <Button type="link">{`${keys.length} ${t('variables')}`}</Button>
            </Tooltip>
          );
        },
      },
      {
        title: t('table.createdAt'),
        dataIndex: 'createdAt',
        render: (value: string) => value && dateFormat(value),
      },
      {
        title: t('table.sentAt'),
        dataIndex: 'sentAt',
        render: (value: string) => value && dateFormat(value),
      },
      {
        title: t('table.createdBy'),
        dataIndex: 'createdBy',
      },
    ];

    return defaultColumns;
  }, [t, smsDescriptionLink]);

  const handleDateChange = useCallback(
    (values: any) => {
      const start = moment(values.startDate).startOf('day');
      const end = moment(values.endDate).endOf('day');
      setSearchParams({
        sentAtFrom: toISOStringWithOffset(start),
        sentAtTo: toISOStringWithOffset(end),
      });
    },
    [setSearchParams],
  );

  const tableFilters = useMemo(
    () => [
      {
        visible: true,
        label: t('table.msisdn'),
        element: (
          <Search
            onSearch={value => setSearchParams({ msisdn: value })}
            onChange={({ target: { value } }) => setMsisdnValue(value)}
            value={msisdnValue}
            allowClear
          />
        ),
      },
      {
        visible: true,
        label: t('table.sendSmsRequestId'),
        element: (
          <Search
            onSearch={value => setSearchParams({ sendSmsRequestId: value })}
            onChange={
              ({ target: { value } }) => setSendSmsRequestIdValue(value)
              // eslint-disable-next-line react/jsx-curly-newline
            }
            value={sendSmsRequestIdValue}
            allowClear
          />
        ),
      },
      {
        visible: true,
        label: t('table.sentAtTo'),
        element: (
          <ConfigProvider locale={ptBR}>
            <QuickRangePicker
              onChange={handleDateChange}
              defaultValue={
                sentAtFrom
                  ? [
                      moment(sentAtFrom, 'YYYYMMDD'),
                      moment(sentAtTo, 'YYYYMMDD'),
                    ]
                  : undefined
              }
              style={{ width: '100%' }}
            />
          </ConfigProvider>
        ),
      },
      {
        visible: true,
        label: t('table.status'),
        element: (
          <Select
            optionFilterProp="label"
            options={[
              {
                label: t('status-type.PENDING'),
                value: 'PENDING',
              },
              {
                label: t('status-type.UNDELIVERABLE'),
                value: 'UNDELIVERABLE',
              },
              {
                label: t('status-type.DELIVERED'),
                value: 'DELIVERED',
              },
              {
                label: t('status-type.EXPIRED'),
                value: 'EXPIRED',
              },
              {
                label: t('status-type.REJECTED'),
                value: 'REJECTED',
              },
            ]}
            style={{ width: '100%' }}
            allowClear
            showSearch
            value={status}
            onChange={value => setSearchParams({ status: value })}
          />
        ),
      },
    ],
    [
      setSearchParams,
      t,
      sentAtFrom,
      handleDateChange,
      sentAtTo,
      status,
      msisdnValue,
      sendSmsRequestIdValue,
    ],
  );

  useEffect(() => {
    setSendSmsRequestIdValue(sendSmsRequestId);
  }, [sendSmsRequestId]);

  useEffect(() => {
    setMsisdnValue(msisdn);
  }, [msisdn]);

  return (
    <>
      <Table
        title={t('title.details')}
        columns={columns}
        data={data?.content}
        loading={!data}
        pagination={{ page, linesPerPage, total: data?.totalElements }}
        onTableChange={setSearchParams}
        filters={{
          list: tableFilters,
          openByDefault: true,
          hasValuesInFilters: hasValuesInExtraParams,
          onClearFilters: () => clearSearchParams(),
        }}
        tabs={{
          activeKey: tableToShow,
          list: [
            { label: t('tabs.batch'), key: 'batch' },
            { label: t('tabs.one'), key: 'one' },
          ],
          onChange: handleTabsChange,
        }}
        extraActions={
          <>
            <Tooltip placement="top" title={t('title.newSmsSend')}>
              <Button
                icon={<PlusCircleOutlined />}
                size="large"
                type="link"
                onClick={() => setVisible(true)}
              />
            </Tooltip>
            <RequestReport
              items={[
                {
                  role: rolesConstants.REQUEST_REPORT,
                  path: '/service-proxy/sms/unitary/report',
                  type: 'SMS',
                  method: 'get',
                  filters: {
                    sendSmsRequestId,
                    msisdn,
                    sentAtFrom,
                    sentAtTo,
                  },
                },
              ]}
            />
          </>
        }
        tableKey="sms-send-details"
      />
      <DeviceActionModal
        visible={visible}
        actionKey={ActionTypes.SEND_SMS}
        onClose={() => {
          setVisible(false);
          mutate();
        }}
        devicesProps={[]}
      />
    </>
  );
};

export default SmsSendDetails;
